// Save migrations, one version at a time. A save slice (a kernel module's, or any versioned part of a game's save)
// records the version it was written at; on load, the steps between that version and the current one run in order.
// `steps[n]` turns data written at version n - 1 into version n. A missing step, or data newer than the build, throws
// a SaveMigrationError naming the slice and the versions, so an old save never loads half-converted and a newer one
// never loads at all (the caller decides what the player sees). Suggestion: versioned save slices with per-module
// migrations (engine/docs/research/suggestions/runtime-input-kernel.md).

/** A slice's migration steps: `steps[n]` turns data at version n - 1 into data at version n. */
export type MigrationSteps = Readonly<Record<number, (data: unknown) => unknown>>;

/** The version a slice gets when it declares none, and the version a save without a recorded version is read as. */
export const FIRST_SLICE_VERSION = 1 as const;

/** A slice that can't be brought to the current version: no step for a version, or data newer than this build. */
export class SaveMigrationError extends Error {
  constructor(
    readonly slice: string,
    readonly from: number,
    readonly to: number,
    message: string,
  ) {
    super(message);
    this.name = "SaveMigrationError";
  }
}

/** One migration that ran, for reports. */
export interface MigrationRun {
  slice: string;
  from: number;
  to: number;
}

/**
 * Bring `data` written at version `from` to version `to` through `steps`, one version at a time. Returns `data`
 * unchanged when the versions match. Throws {@link SaveMigrationError} when a step is missing or `from > to`.
 */
export function migrateData(slice: string, from: number, to: number, data: unknown, steps: MigrationSteps | undefined): unknown {
  if (!Number.isInteger(from) || !Number.isInteger(to)) throw new SaveMigrationError(slice, from, to, `save slice "${slice}": versions must be integers (${from} → ${to})`);
  if (from === to) return data;
  if (from > to) throw new SaveMigrationError(slice, from, to, `save slice "${slice}" is version ${from}, newer than this build's version ${to}`);
  let d = data;
  for (let v = from + 1; v <= to; v++) {
    const step = steps?.[v];
    if (!step) throw new SaveMigrationError(slice, from, to, `save slice "${slice}" has no migration from version ${v - 1} to ${v}`);
    d = step(d);
  }
  return d;
}
